/**
 * App bar right-hand cluster: external targets and copy.
 *
 * Transcribed from the legacy Vue app's `components/layout/Headerbar.vue`.
 * These are public product URLs, not secrets, so they live in config rather
 * than env — legacy hard-codes them in the component for the same reason. The
 * one exception is the Common Services host (feedback), which differs per
 * environment and so comes from env.
 *
 * Only the demo differs per product; everything else is shared.
 */
import { env } from '@config/env';

/**
 * The in-product demo, opened in a full-screen dialog.
 * Legacy: `demoUrl` computed, CCA vs EIA CloudFront distributions.
 */
export const DEMO_URLS = Object.freeze({
  cca: 'https://d1dedbwm6ntaya.cloudfront.net/',
  eia: 'https://d2008bczhvnw5c.cloudfront.net/',
});

/**
 * Meta Collector — EIA-only in legacy, opened in a new tab. ALWAYS
 * VITE_METACOLLECTOR_URL, with no fallback (user order 2026-10-22): the host
 * differs per environment; when it is unset the button says it is not
 * available instead of opening a guessed host.
 */
export const META_COLLECTOR_URL = env.metaCollectorUrl;

/**
 * Feedback target — Common Services, NOT this app.
 *
 * Legacy builds it as `${VITE_OKTA_POSTLOGOUT_REDIRECT_URI}/feedback`
 * (Headerbar.vue:453-454) and opens it with `window.open(url, '_blank')`.
 *
 * An earlier version of this file read that env var as "the app's own origin"
 * and shipped a same-origin `/feedback` path. It is not: the post-logout
 * redirect points at the Common Services host, so the button has to leave this
 * app entirely. A relative path just 404s inside CCA/EIA.
 *
 * Built from the same env var legacy uses (`env.csHomeUrl`), because that host
 * differs per environment — legacy's dev value is dev.epycadvisory.amd.com — so
 * a hard-coded host is right for exactly one environment. It was hard-coded to
 * the production host here before that var was read; dev would have sent people
 * to prod feedback. The production host stays as the fallback for when the var
 * is unset.
 */
const CS_HOME_FALLBACK = 'https://csepyc.amd.com';
const CS_HOME = (env.csHomeUrl || CS_HOME_FALLBACK).replace(/\/+$/, '');
export const FEEDBACK_URL = `${CS_HOME}/feedback`;

/**
 * Profile menu → "Profile": Common Services' request-role page, in the SAME
 * tab, with `?appName=CCA|EIA` added (legacy redirectToProfile,
 * Headerbar.vue:618-622, `${VITE_PROFILE_REDIRECT_URI}?appName=${app}`).
 * ALWAYS VITE_PROFILE_REDIRECT_URI, with no fallback (user order 2026-10-22):
 * when it is unset the Profile item says it is not available in this
 * environment (HeaderActions), rather than guessing a host.
 */
export const PROFILE_URL = env.csProfileUrl;

/** Verbatim copy from legacy. */
export const NOTIFICATIONS_EMPTY = 'No new notifications.';

/**
 * Signed-in user.
 *
 * HARD-CODED placeholder. Legacy reads `Cookies.get('username')` into Vuex
 * `userName` and renders it raw — it is a username, not a formatted display
 * name, which is why it looks like an email. Replace with the auth slice's
 * user once a real login exists.
 */
export const PLACEHOLDER_USER_EMAIL = 'shaimoha@amd.com';

/**
 * Profile menu items, in legacy's order.
 *
 * "Online Documentation" is left out of the menu on a page that has no help
 * topic (components/common/OnlineDocs/helpTopics.js). Legacy hid it only on
 * the support, regions and analytics pages and opened an EMPTY dialog
 * everywhere else it had nothing for.
 *
 * No "Support" item: legacy's desktop menu has none (the headset icon is the
 * only desktop entry, so a menu item would be a duplicate — #10566).
 *
 * Not reproduced: legacy's MOBILE menu reads "Admin Panel" for Profile when the
 * role is super_admin / super_admin_analytics; its desktop menu never does.
 */
export const PROFILE_MENU = Object.freeze([
  { id: 'profile', label: 'Profile' },
  { id: 'docs', label: 'Online Documentation' },
  { id: 'about', label: 'About' },
  { id: 'logout', label: 'Logout', danger: true, separatorBefore: true },
]);
